import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  TextField,
  Switch,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  Chip,
  Slider,
} from '@mui/material';
import { Settings, X, Plus, Trash2, ExternalLink, Save, CheckCircle2, Sparkles } from 'lucide-react';
import validations, { extractYoutubeVideoId, fetchYoutubeVideoTitle } from '../../pages/helpers/validations';
import { useTranslation } from 'react-i18next';

/**
 * ConfigModal
 * Modal de pantalla casi completa con fondo difuminado y oscurecido.
 * Estilo gamer neón, navegación en solapas, gestión dinámica de intros y transiciones (mínimo 1)
 * y soporte para enlaces completos de YouTube o códigos de 11 caracteres con placeholders claros.
 */
export default function ConfigModal({
  open,
  onClose,
  initialTab = 0,
  auth,
  user,
  userSettings,
  saveUserSettings,
  setAllowInteractivity,
  allowInteractivity,
  setShowControlsPlayer,
  showControlsPlayer,
  setSnackMessage,
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab);

  // Helper para normalizar la lista de intros/transiciones a objetos { id, name }
  const normalizeItems = (list, defaultPrefix) => {
    if (!Array.isArray(list) || list.length === 0) {
      return [{ id: '', name: '' }];
    }
    return list.map((item, idx) => {
      if (typeof item === 'object' && item !== null) {
        return {
          id: item.id || '',
          name: item.name !== undefined ? item.name.substring(0, 50) : `${defaultPrefix} ${idx + 1}`.substring(0, 50),
        };
      }
      return {
        id: item || '',
        name: item ? `${defaultPrefix} ${idx + 1}`.substring(0, 50) : '',
      };
    });
  };

  // Estados locales para edición dentro del modal
  const [localControlsPlayer, setLocalControlsPlayer] = useState(showControlsPlayer ?? true);
  const [localInteractivity, setLocalInteractivity] = useState(allowInteractivity ?? true);
  const [localMonitorSyncOffsetSec, setLocalMonitorSyncOffsetSec] = useState(0);
  const [localMonitorEnabled, setLocalMonitorEnabled] = useState(true);
  const [apiKeys, setApiKeys] = useState(['']);
  const [intros, setIntros] = useState([{ id: '', name: '' }]);
  const [transitions, setTransitions] = useState([{ id: '', name: '' }]);
  const [loadingTitles, setLoadingTitles] = useState({});

  // Sincronizar estados cuando se abre el modal o cambian los settings
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      setLocalControlsPlayer(showControlsPlayer ?? true);
      setLocalInteractivity(allowInteractivity ?? true);
      const offsetVal = userSettings?.monitorSyncOffsetSec ?? userSettings?.configs?.general?.monitorSyncOffsetSec ?? 0;
      setLocalMonitorSyncOffsetSec(offsetVal);
      const enabledVal = (userSettings?.isMonitorEnabled ?? userSettings?.configs?.general?.isMonitorEnabled) !== false;
      setLocalMonitorEnabled(enabledVal);

      const existingApiKeys = userSettings?.configs?.apiKeys;
      setApiKeys(
        existingApiKeys && existingApiKeys.length > 0
          ? [...existingApiKeys]
          : Array(5).fill('')
      );

      const existingIntros = userSettings?.configs?.intros;
      setIntros(
        existingIntros && existingIntros.length > 0
          ? normalizeItems(existingIntros, 'Intro')
          : [{ id: process.env.VITE_DEFAULT_INTRO_VIDEO_CODE || 'H_bB0sAqLNg', name: 'Intro por defecto' }]
      );

      const existingTransitions = userSettings?.configs?.transitions;
      setTransitions(
        existingTransitions && existingTransitions.length > 0
          ? normalizeItems(existingTransitions, 'Transición')
          : [{ id: process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY', name: 'Transición por defecto' }]
      );
    }
  }, [open, initialTab, showControlsPlayer, allowInteractivity, userSettings]);

  // Handlers para API Keys
  const handleApiKeyChange = (index, value) => {
    const updated = [...apiKeys];
    updated[index] = value;
    setApiKeys(updated);
  };

  const handleAddApiKey = () => {
    setApiKeys([...apiKeys, '']);
  };

  const handleRemoveApiKey = (index) => {
    if (apiKeys.length <= 1) return;
    const updated = apiKeys.filter((_, i) => i !== index);
    setApiKeys(updated);
  };

  // Handlers para Intros (mínimo 1 obligatoria) con auto-importación de título (máximo 50 caracteres)
  const handleIntroIdChange = async (index, value) => {
    const updated = [...intros];
    const prevId = extractYoutubeVideoId(updated[index]?.id || '');
    updated[index] = { ...updated[index], id: value };
    setIntros(updated);

    const newId = extractYoutubeVideoId(value);
    if (newId && newId.length === 11 && newId !== prevId) {
      setLoadingTitles((prev) => ({ ...prev, [`intro_${index}`]: true }));
      try {
        const title = await fetchYoutubeVideoTitle(newId);
        if (title) {
          const truncatedTitle = title.trim().substring(0, 50);
          setIntros((current) => {
            const next = [...current];
            if (next[index]) {
              next[index] = { ...next[index], name: truncatedTitle };
            }
            return next;
          });
        }
      } catch (err) {
        console.warn('Error al auto-importar título de intro:', err);
      } finally {
        setLoadingTitles((prev) => ({ ...prev, [`intro_${index}`]: false }));
      }
    }
  };

  const handleIntroNameChange = (index, value) => {
    const updated = [...intros];
    updated[index] = { ...updated[index], name: (value || '').substring(0, 50) };
    setIntros(updated);
  };

  const handleAddIntro = () => {
    setIntros([...intros, { id: '', name: '' }]);
  };

  const handleRemoveIntro = (index) => {
    if (intros.length <= 1) return;
    const updated = intros.filter((_, i) => i !== index);
    setIntros(updated);
  };

  // Handlers para Transiciones (mínimo 1 obligatoria) con auto-importación de título (máximo 50 caracteres)
  const handleTransitionIdChange = async (index, value) => {
    const updated = [...transitions];
    const prevId = extractYoutubeVideoId(updated[index]?.id || '');
    updated[index] = { ...updated[index], id: value };
    setTransitions(updated);

    const newId = extractYoutubeVideoId(value);
    if (newId && newId.length === 11 && newId !== prevId) {
      setLoadingTitles((prev) => ({ ...prev, [`transition_${index}`]: true }));
      try {
        const title = await fetchYoutubeVideoTitle(newId);
        if (title) {
          const truncatedTitle = title.trim().substring(0, 50);
          setTransitions((current) => {
            const next = [...current];
            if (next[index]) {
              next[index] = { ...next[index], name: truncatedTitle };
            }
            return next;
          });
        }
      } catch (err) {
        console.warn('Error al auto-importar título de transición:', err);
      } finally {
        setLoadingTitles((prev) => ({ ...prev, [`transition_${index}`]: false }));
      }
    }
  };

  const handleTransitionNameChange = (index, value) => {
    const updated = [...transitions];
    updated[index] = { ...updated[index], name: (value || '').substring(0, 50) };
    setTransitions(updated);
  };

  const handleAddTransition = () => {
    setTransitions([...transitions, { id: '', name: '' }]);
  };

  const handleRemoveTransition = (index) => {
    if (transitions.length <= 1) return;
    const updated = transitions.filter((_, i) => i !== index);
    setTransitions(updated);
  };

  // Validación en tiempo real
  const apiKeysErrors = validations.checkApiKeysErrors(apiKeys);
  const introsErrors = validations.checkIntrosErrors(intros);
  const transitionsErrors = validations.checkTransitionsErrors(transitions);

  const hasAnyError =
    Object.values(apiKeysErrors).some((e) => e.error) ||
    Object.values(introsErrors).some((e) => e.error) ||
    Object.values(transitionsErrors).some((e) => e.error);

  // Guardar configuración completa en Firebase
  const handleSave = () => {
    if (hasAnyError) {
      if (setSnackMessage) {
        setSnackMessage({
          text: 'Por favor, corrige los campos con formato inválido o nombres vacíos antes de guardar.',
          type: 'error',
          duration: 6000,
        });
      }
      return;
    }

    // Normalizar intros y transiciones extrayendo el ID limpio de 11 caracteres y nombre limpio limitado a 50 caracteres
    const normalizedIntros = intros
      .map((item) => ({
        id: item.id ? extractYoutubeVideoId(item.id) : '',
        name: item.name ? item.name.trim().substring(0, 50) : '',
      }))
      .filter((item) => item.id || item.name);

    const normalizedTransitions = transitions
      .map((item) => ({
        id: item.id ? extractYoutubeVideoId(item.id) : '',
        name: item.name ? item.name.trim().substring(0, 50) : '',
      }))
      .filter((item) => item.id || item.name);

    const normalizedApiKeys = apiKeys.map((k) => k.trim());

    // Actualizar estados globales de UI
    setShowControlsPlayer(localControlsPlayer);
    setAllowInteractivity(localInteractivity);

    const newUserSettings = {
      ...(userSettings || {}),
      isMonitorEnabled: localMonitorEnabled,
      monitorSyncOffsetSec: Number(localMonitorSyncOffsetSec) || 0,
      configs: {
        ...(userSettings?.configs || {}),
        general: {
          ...(userSettings?.configs?.general || {}),
          showControlsPlayer: localControlsPlayer,
          allowInteractivity: localInteractivity,
          isMonitorEnabled: localMonitorEnabled,
          monitorSyncOffsetSec: Number(localMonitorSyncOffsetSec) || 0,
          theme: 'dark',
        },
        apiKeys: normalizedApiKeys,
        intros: normalizedIntros,
        transitions: normalizedTransitions,
      },
    };

    if (user && user.uid && saveUserSettings) {
      saveUserSettings(user.uid, newUserSettings);
    }

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            background: 'rgba(6, 1, 14, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: '95vw',
          maxWidth: '1150px',
          height: '88vh',
          maxHeight: '850px',
          borderRadius: '22px',
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          WebkitBackdropFilter: 'none !important',
          border: '1px solid rgba(255, 45, 155, 0.35)',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.98), 0 0 35px rgba(255, 45, 155, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          m: 'auto',
        },
      }}
    >
      {/* Header del Modal */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2.5, md: 3.5 },
          py: 2,
          mb: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          background: '#150a26',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.2) 0%, rgba(180, 77, 255, 0.2) 100%)',
              border: '1px solid rgba(255, 45, 155, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ff2d9b',
              boxShadow: '0 0 15px rgba(255, 45, 155, 0.3)',
            }}
          >
            <Settings size={20} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.05rem', md: '1.25rem' },
                fontWeight: 700,
                color: '#ffffff',
                lineHeight: 1.2,
                fontFamily: "'Outfit', sans-serif",
              }}
            >
              {t('config.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.55)', mt: 0.2 }}>
              {t('config.generalTitle')}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t('common.close')}
          sx={{
            color: 'rgba(255, 255, 255, 0.65)',
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '10px',
            p: '7px',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: '#ffffff',
              background: 'rgba(255, 45, 155, 0.2)',
              borderColor: 'rgba(255, 45, 155, 0.5)',
              transform: 'scale(1.05)',
            },
          }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      {/* Barra de Solapas (Tabs) */}
      <Box
        sx={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'rgba(0, 0, 0, 0.25)',
          px: { xs: 1.5, md: 3 },
          flexShrink: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(90deg, #ff2d9b 0%, #b44dff 100%)',
              height: 3,
              borderRadius: '3px 3px 0 0',
              boxShadow: '0 0 12px #ff2d9b',
            },
          }}
        >
          <Tab
            label={t('config.tabGeneral')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
              color: 'rgba(255, 255, 255, 0.65)',
              '&.Mui-selected': { color: '#ffffff' },
              transition: 'color 0.2s ease',
            }}
          />
          <Tab
            label={t('config.tabApiKeys')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
              color: 'rgba(255, 255, 255, 0.65)',
              '&.Mui-selected': { color: '#ffffff' },
              transition: 'color 0.2s ease',
            }}
          />
          <Tab
            label={`${t('config.tabIntros')} (${intros.filter(i => (i.id || i.name)).length})`}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
              color: 'rgba(255, 255, 255, 0.65)',
              '&.Mui-selected': { color: '#ffffff' },
              transition: 'color 0.2s ease',
            }}
          />
          <Tab
            label={`${t('config.tabTransitions')} (${transitions.filter(tItem => (tItem.id || tItem.name)).length})`}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
              color: 'rgba(255, 255, 255, 0.65)',
              '&.Mui-selected': { color: '#ffffff' },
              transition: 'color 0.2s ease',
            }}
          />
        </Tabs>
      </Box>

      {/* Contenido scrolleable del tab */}
      <DialogContent
        className="config-modal-body"
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: { xs: 2, md: 3.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 45, 155, 0.45) rgba(0, 0, 0, 0.35)',
          '&::-webkit-scrollbar': {
            width: '7px',
            height: '7px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.35)',
            borderRadius: '999px',
            margin: '4px 0',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(180deg, rgba(255, 45, 155, 0.45) 0%, rgba(180, 77, 255, 0.45) 100%)',
            borderRadius: '999px',
            boxShadow: '0 0 8px rgba(255, 45, 155, 0.25)',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'linear-gradient(180deg, #ff2d9b 0%, #b44dff 100%)',
            boxShadow: '0 0 12px rgba(255, 45, 155, 0.55)',
          },
        }}
      >
        {/* SOLAPA 0: GENERALES */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                transition: 'all 0.25s ease',
                '&:hover': {
                  borderColor: 'rgba(255, 45, 155, 0.3)',
                  background: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#ffffff' }}>
                  {t('config.showControls')}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                  {t('config.screenTitle')}
                </Typography>
              </Box>
              <Switch
                checked={localControlsPlayer}
                onChange={(e) => setLocalControlsPlayer(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#ff2d9b',
                    '&:hover': { backgroundColor: 'rgba(255, 45, 155, 0.15)' },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#b44dff',
                    opacity: 0.85,
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                p: 2.5,
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                transition: 'all 0.25s ease',
                '&:hover': {
                  borderColor: 'rgba(255, 45, 155, 0.3)',
                  background: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#ffffff' }}>
                  {t('config.allowInteractivity')}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                  {t('screenControls.qrTooltip')}
                </Typography>
              </Box>
              <Switch
                checked={localInteractivity}
                onChange={(e) => setLocalInteractivity(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#ff2d9b',
                    '&:hover': { backgroundColor: 'rgba(255, 45, 155, 0.15)' },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#b44dff',
                    opacity: 0.85,
                  },
                }}
              />
            </Box>

            {/* Habilitar / Deshabilitar Monitores (/monitor) */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                transition: 'all 0.25s ease',
                '&:hover': {
                  borderColor: 'rgba(0, 212, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#ffffff' }}>
                  Habilitar Pantallas de Monitor para Invitados (/monitor)
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                  Permite a los invitados seguir canciones y votar desde sus celulares. Si experimentas problemas de red o ancho de banda, puedes pausarlo temporalmente.
                </Typography>
              </Box>
              <Switch
                checked={localMonitorEnabled}
                onChange={(e) => setLocalMonitorEnabled(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#00d4ff',
                    '&:hover': { backgroundColor: 'rgba(0, 212, 255, 0.15)' },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#00d4ff',
                    opacity: 0.85,
                  },
                }}
              />
            </Box>

            {/* Tolerancia / Desfasaje de Monitores */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                transition: 'all 0.25s ease',
                '&:hover': {
                  borderColor: 'rgba(0, 212, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#ffffff' }}>
                    Tolerancia / Desfasaje de Monitores
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                    Ajuste fino en segundos para los monitores vinculados. Se aplica en vivo en todos los dispositivos conectados.
                  </Typography>
                </Box>
                <Chip
                  label={`${localMonitorSyncOffsetSec > 0 ? '+' : ''}${localMonitorSyncOffsetSec}s`}
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    backgroundColor: 'rgba(0, 212, 255, 0.15)',
                    color: '#00d4ff',
                    border: '1px solid rgba(0, 212, 255, 0.4)',
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, mt: 0.5 }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.4)' }}>
                  -10s (retrasar)
                </Typography>
                <Slider
                  value={Number(localMonitorSyncOffsetSec) || 0}
                  onChange={(_, val) => setLocalMonitorSyncOffsetSec(val)}
                  min={-10}
                  max={10}
                  step={0.5}
                  sx={{
                    color: '#00d4ff',
                    '& .MuiSlider-thumb': {
                      boxShadow: '0 0 10px #00d4ff',
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    },
                  }}
                />
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.4)' }}>
                  +10s (adelantar)
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* SOLAPA 1: API KEYS DE YOUTUBE */}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2.2,
                borderRadius: '14px',
                background: 'rgba(180, 77, 255, 0.08)',
                border: '1px solid rgba(180, 77, 255, 0.25)',
              }}
            >
              <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.5 }}>
                Establece una o más API Keys de YouTube para habilitar las búsquedas de videos musicales. Como YouTube impone una cuota diaria por clave, te aconsejamos configurar varias para evitar interrupciones en tus eventos.
              </Typography>
              <Box sx={{ mt: 1.2 }}>
                <Button
                  component="a"
                  href="https://console.cloud.google.com/apis/credentials"
                  target="_blank"
                  rel="noreferrer"
                  size="small"
                  startIcon={<ExternalLink size={14} />}
                  sx={{
                    color: '#b44dff',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    p: 0,
                    '&:hover': { textDecoration: 'underline', background: 'transparent' },
                  }}
                >
                  Obtener credenciales en Google Cloud Console
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {apiKeys.map((key, index) => (
                <Box key={`apikey_${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`API Key #${index + 1}`}
                    placeholder="AIzaSy..."
                    value={key}
                    onChange={(e) => handleApiKeyChange(index, e.target.value)}
                    error={apiKeysErrors[`apikey_${index}`]?.error}
                    helperText={apiKeysErrors[`apikey_${index}`]?.errorMsg}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        background: 'rgba(255, 255, 255, 0.04)',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 45, 155, 0.4)' },
                        '&.Mui-focused fieldset': { borderColor: '#ff2d9b' },
                      },
                    }}
                  />
                  {apiKeys.length > 1 && (
                    <Tooltip title="Eliminar esta clave">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveApiKey(index)}
                        sx={{
                          color: '#ff4d6d',
                          background: 'rgba(255, 77, 109, 0.08)',
                          border: '1px solid rgba(255, 77, 109, 0.2)',
                          borderRadius: '8px',
                          p: '8px',
                          '&:hover': { background: 'rgba(255, 77, 109, 0.2)' },
                        }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}

              <Button
                variant="outlined"
                startIcon={<Plus size={16} />}
                onClick={handleAddApiKey}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  borderRadius: '10px',
                  color: '#b44dff',
                  borderColor: 'rgba(180, 77, 255, 0.4)',
                  fontSize: '0.82rem',
                  py: 0.8,
                  px: 2,
                  mt: 0.5,
                  '&:hover': {
                    borderColor: '#b44dff',
                    background: 'rgba(180, 77, 255, 0.1)',
                  },
                }}
              >
                Agregar otra API Key
              </Button>
            </Box>
          </Box>
        )}

        {/* SOLAPA 2: INTROS */}
        {activeTab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2.2,
                borderRadius: '14px',
                background: 'rgba(255, 45, 155, 0.08)',
                border: '1px solid rgba(255, 45, 155, 0.25)',
              }}
            >
              <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.5 }}>
                Aquí defines los videos de apertura para el inicio de tu fiesta de karaoke. Puedes ingresar el <strong>enlace completo de YouTube</strong> o el <strong>código de 11 caracteres</strong>. El sistema importará automáticamente el título original, el cual puedes editar a tu gusto (el nombre no puede quedar vacío).
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
              {intros.map((item, index) => {
                const extracted = item.id ? extractYoutubeVideoId(item.id) : '';
                const isSingle = intros.length <= 1;
                const fieldError = introsErrors[`intro_${index}`];
                const isNameEmpty = item.id && (!item.name || !item.name.trim());
                const isLoadingTitle = Boolean(loadingTitles[`intro_${index}`]);

                return (
                  <Box
                    key={`intro_${index}`}
                    sx={{
                      p: 2,
                      borderRadius: '14px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: isNameEmpty ? '1px solid rgba(244, 67, 54, 0.5)' : '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: isNameEmpty ? '#f44336' : 'rgba(255, 45, 155, 0.3)',
                        background: 'rgba(255, 255, 255, 0.04)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: '#ff2d9b' }}>
                          Intro #{index + 1}
                        </Typography>
                        {isLoadingTitle && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'rgba(255, 255, 255, 0.5)' }}>
                            <CircularProgress size={12} sx={{ color: '#ff2d9b' }} />
                            <Typography sx={{ fontSize: '0.72rem' }}>Importando título original...</Typography>
                          </Box>
                        )}
                      </Box>

                      <Tooltip
                        title={
                          isSingle
                            ? 'Debe existir al menos una intro obligatoria (no se puede eliminar).'
                            : 'Eliminar esta intro'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={isSingle}
                            onClick={() => handleRemoveIntro(index)}
                            sx={{
                              color: '#ff4d6d',
                              background: 'rgba(255, 77, 109, 0.08)',
                              border: '1px solid rgba(255, 77, 109, 0.2)',
                              borderRadius: '8px',
                              p: '6px',
                              '&:hover': { background: 'rgba(255, 77, 109, 0.2)' },
                              '&.Mui-disabled': {
                                opacity: 0.3,
                                borderColor: 'rgba(255, 255, 255, 0.08)',
                                color: 'rgba(255, 255, 255, 0.3)',
                              },
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Video de YouTube (URL o Código)"
                          placeholder="Ej: https://www.youtube.com/watch?v=H_bB0sAqLNg o H_bB0sAqLNg"
                          value={item.id}
                          onChange={(e) => handleIntroIdChange(index, e.target.value)}
                          error={fieldError?.error && fieldError?.errorMsg !== 'El nombre es obligatorio y no puede quedar vacío.'}
                          helperText={
                            fieldError?.error && fieldError?.errorMsg !== 'El nombre es obligatorio y no puede quedar vacío.'
                              ? fieldError.errorMsg
                              : extracted && extracted !== item.id
                                ? `ID detectado: ${extracted}`
                                : 'Pega el enlace o código para importar el título.'
                          }
                          InputProps={{
                            endAdornment: isLoadingTitle ? (
                              <InputAdornment position="end">
                                <CircularProgress size={16} sx={{ color: '#ff2d9b' }} />
                              </InputAdornment>
                            ) : null,
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                              background: 'rgba(255, 255, 255, 0.04)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
                              '&:hover fieldset': { borderColor: 'rgba(255, 45, 155, 0.4)' },
                              '&.Mui-focused fieldset': { borderColor: '#ff2d9b' },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Nombre de la Intro (Obligatorio)"
                          placeholder="Ej: Conteo regresivo 10s"
                          value={item.name}
                          onChange={(e) => handleIntroNameChange(index, e.target.value)}
                          inputProps={{ maxLength: 50 }}
                          error={isNameEmpty}
                          helperText={
                            isNameEmpty
                              ? 'El nombre no puede quedar vacío.'
                              : `Nombre visible en los desplegables (${item.name ? item.name.length : 0}/50 caracteres).`
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                              background: 'rgba(255, 255, 255, 0.04)',
                              '& fieldset': { borderColor: isNameEmpty ? '#f44336' : 'rgba(255, 255, 255, 0.12)' },
                              '&:hover fieldset': { borderColor: isNameEmpty ? '#f44336' : 'rgba(255, 45, 155, 0.4)' },
                              '&.Mui-focused fieldset': { borderColor: isNameEmpty ? '#f44336' : '#ff2d9b' },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}

              <Button
                variant="outlined"
                startIcon={<Plus size={16} />}
                onClick={handleAddIntro}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  borderRadius: '10px',
                  color: '#ff2d9b',
                  borderColor: 'rgba(255, 45, 155, 0.4)',
                  fontSize: '0.82rem',
                  py: 0.8,
                  px: 2,
                  mt: 0.5,
                  '&:hover': {
                    borderColor: '#ff2d9b',
                    background: 'rgba(255, 45, 155, 0.1)',
                  },
                }}
              >
                Agregar otra Intro
              </Button>
            </Box>
          </Box>
        )}

        {/* SOLAPA 3: TRANSICIONES */}
        {activeTab === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2.2,
                borderRadius: '14px',
                background: 'rgba(0, 212, 255, 0.08)',
                border: '1px solid rgba(0, 212, 255, 0.25)',
              }}
            >
              <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.5 }}>
                Videos de transición que se activan al terminar una canción para mantener la pantalla siempre activa y divertida. Acepta el <strong>enlace completo de YouTube</strong> o el <strong>código de 11 caracteres</strong>. El sistema importará automáticamente el título original, el cual puedes editar a tu gusto (el nombre no puede quedar vacío).
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
              {transitions.map((item, index) => {
                const extracted = item.id ? extractYoutubeVideoId(item.id) : '';
                const isSingle = transitions.length <= 1;
                const fieldError = transitionsErrors[`transition_${index}`];
                const isNameEmpty = item.id && (!item.name || !item.name.trim());
                const isLoadingTitle = Boolean(loadingTitles[`transition_${index}`]);

                return (
                  <Box
                    key={`transition_${index}`}
                    sx={{
                      p: 2,
                      borderRadius: '14px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: isNameEmpty ? '1px solid rgba(244, 67, 54, 0.5)' : '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: isNameEmpty ? '#f44336' : 'rgba(0, 212, 255, 0.3)',
                        background: 'rgba(255, 255, 255, 0.04)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: '#00d4ff' }}>
                          Transición #{index + 1}
                        </Typography>
                        {isLoadingTitle && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'rgba(255, 255, 255, 0.5)' }}>
                            <CircularProgress size={12} sx={{ color: '#00d4ff' }} />
                            <Typography sx={{ fontSize: '0.72rem' }}>Importando título original...</Typography>
                          </Box>
                        )}
                      </Box>

                      <Tooltip
                        title={
                          isSingle
                            ? 'Debe existir al menos una transición obligatoria (no se puede eliminar).'
                            : 'Eliminar esta transición'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={isSingle}
                            onClick={() => handleRemoveTransition(index)}
                            sx={{
                              color: '#ff4d6d',
                              background: 'rgba(255, 77, 109, 0.08)',
                              border: '1px solid rgba(255, 77, 109, 0.2)',
                              borderRadius: '8px',
                              p: '6px',
                              '&:hover': { background: 'rgba(255, 77, 109, 0.2)' },
                              '&.Mui-disabled': {
                                opacity: 0.3,
                                borderColor: 'rgba(255, 255, 255, 0.08)',
                                color: 'rgba(255, 255, 255, 0.3)',
                              },
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Video de YouTube (URL o Código)"
                          placeholder="Ej: https://www.youtube.com/watch?v=wpXZZ4f_RKY o wpXZZ4f_RKY"
                          value={item.id}
                          onChange={(e) => handleTransitionIdChange(index, e.target.value)}
                          error={fieldError?.error && fieldError?.errorMsg !== 'El nombre es obligatorio y no puede quedar vacío.'}
                          helperText={
                            fieldError?.error && fieldError?.errorMsg !== 'El nombre es obligatorio y no puede quedar vacío.'
                              ? fieldError.errorMsg
                              : extracted && extracted !== item.id
                                ? `ID detectado: ${extracted}`
                                : 'Pega el enlace o código para importar el título.'
                          }
                          InputProps={{
                            endAdornment: isLoadingTitle ? (
                              <InputAdornment position="end">
                                <CircularProgress size={16} sx={{ color: '#00d4ff' }} />
                              </InputAdornment>
                            ) : null,
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                              background: 'rgba(255, 255, 255, 0.04)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
                              '&:hover fieldset': { borderColor: 'rgba(0, 212, 255, 0.4)' },
                              '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Nombre de la Transición (Obligatorio)"
                          placeholder="Ej: Cortina animada"
                          value={item.name}
                          onChange={(e) => handleTransitionNameChange(index, e.target.value)}
                          inputProps={{ maxLength: 50 }}
                          error={isNameEmpty}
                          helperText={
                            isNameEmpty
                              ? 'El nombre no puede quedar vacío.'
                              : `Nombre visible en los desplegables (${item.name ? item.name.length : 0}/50 caracteres).`
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                              background: 'rgba(255, 255, 255, 0.04)',
                              '& fieldset': { borderColor: isNameEmpty ? '#f44336' : 'rgba(255, 255, 255, 0.12)' },
                              '&:hover fieldset': { borderColor: isNameEmpty ? '#f44336' : 'rgba(0, 212, 255, 0.4)' },
                              '&.Mui-focused fieldset': { borderColor: isNameEmpty ? '#f44336' : '#00d4ff' },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}

              <Button
                variant="outlined"
                startIcon={<Plus size={16} />}
                onClick={handleAddTransition}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  borderRadius: '10px',
                  color: '#00d4ff',
                  borderColor: 'rgba(0, 212, 255, 0.4)',
                  fontSize: '0.82rem',
                  py: 0.8,
                  px: 2,
                  mt: 0.5,
                  '&:hover': {
                    borderColor: '#00d4ff',
                    background: 'rgba(0, 212, 255, 0.1)',
                  },
                }}
              >
                Agregar otra Transición
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Footer del Modal */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1.5,
          px: { xs: 2.5, md: 3.5 },
          py: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'rgba(255, 255, 255, 0.02)',
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            color: 'rgba(255, 255, 255, 0.75)',
            borderColor: 'rgba(255, 255, 255, 0.18)',
            fontSize: '0.88rem',
            px: 2.5,
            py: 0.8,
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.35)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#ffffff',
            },
          }}
        >
          {t('common.cancel')}
        </Button>

        <Button
          variant="contained"
          startIcon={<Save size={16} />}
          onClick={handleSave}
          disabled={hasAnyError}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #ff2d9b 0%, #b44dff 100%)',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '0.88rem',
            px: 3,
            py: 0.8,
            boxShadow: '0 4px 18px rgba(255, 45, 155, 0.45)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #ff40a7 0%, #be61ff 100%)',
              boxShadow: '0 6px 24px rgba(255, 45, 155, 0.65)',
              transform: 'scale(1.02)',
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
            '&.Mui-disabled': {
              opacity: 0.4,
              color: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          {t('config.saveButton')}
        </Button>
      </Box>
    </Dialog>
  );
}
